import { AlertCircle, AlertTriangle, HandCoins, Repeat, TrendingUp } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCardField } from "@/shared/components/forms/ColocaChoiceCardField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { corridorTripleKey, providersForCorridor } from "../../constants/paymentRoutes.constants";
import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useRouteMutations } from "../../hooks/useRouteMutations";
import { EconomicModel } from "../../types/paymentFees.types";
import type {
  CorridorRouteCell,
  CreatePaymentRoutePayload,
  ProviderRailRoute,
} from "../../types/paymentRoutes.types";
import { corridorCellLabel } from "../../utils/routeFormat";
import { activeRouteCountByProvider } from "../../utils/routeMatrix";
import { ProviderChoiceField } from "./ProviderChoiceField";
import { buildRouteFormSchema, type RouteFormValues } from "./routeFormSchema";

export interface SwitchProviderDialogProps {
  /** Cell being re-pointed — always one with a `route`. */
  cell: CorridorRouteCell;
  open: boolean;
  onClose: () => void;
  routes: ProviderRailRoute[];
  /** Both halves succeeded. */
  onSwitched: (corridorLabel: string) => void;
  /**
   * A terminal outcome the caller must reload after — including the failure that left the corridor unrouted,
   * so the table stops claiming the old route still exists.
   */
  onFinished: () => void;
}

/**
 * Re-points a corridor at another provider.
 *
 * There is no update endpoint and the corridor's unique index ignores `deleted_at IS NULL` only — so a second
 * row cannot coexist with the first. Switching is therefore DELETE **then** POST, exactly as the endpoint's
 * own documentation states, and this dialog owns that sequence rather than leaving the operator to run it by
 * hand.
 *
 * The two honest consequences are stated up front and handled explicitly:
 *
 * - Between the two calls the corridor has no route, and operations landing in that window are refused. The
 *   warning says so before the operator commits.
 * - If the POST fails after the DELETE succeeded, the corridor is left unrouted. The dialog does NOT close: it
 *   switches to a blocking recovery state that retries only the creation, with the payload it already built.
 *   Closing it is still possible, but the caller reloads either way, so the table shows the corridor as
 *   unrouted rather than pretending nothing happened.
 */
export function SwitchProviderDialog({
  cell,
  open,
  onClose,
  routes,
  onSwitched,
  onFinished,
}: SwitchProviderDialogProps) {
  const { t } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();
  const { isSubmitting, error, createRoute, removeRoute } = useRouteMutations();

  /** `form` until the delete succeeded and the create did not — then the corridor is unrouted. */
  const [phase, setPhase] = useState<"form" | "orphaned">("form");
  /** Payload of the pending creation, so the retry never has to rebuild it from a form that may have reset. */
  const pendingPayload = useRef<CreatePaymentRoutePayload | null>(null);

  const currentRoute = cell.route;
  const corridorLabel = corridorCellLabel(cell, labels.directionShort);
  const corridorCountByProvider = useMemo(() => activeRouteCountByProvider(routes), [routes]);

  const schema = useMemo(
    () =>
      buildRouteFormSchema({
        corridorRequired: t("validation_corridor_required"),
        providerRequired: t("validation_provider_required"),
      }),
    [t],
  );

  const form = useZodForm(schema, {
    // The corridor is fixed by the cell; only the provider and the model are being decided.
    corridorKey: corridorTripleKey(cell),
    direction: cell.direction,
    providerCode: "",
    economicModel: currentRoute?.economic_model ?? EconomicModel.PASS_THROUGH,
  });

  const values = form.watch();

  /**
   * The current provider is only excluded while its route is ACTIVE — there, re-selecting it would delete and
   * recreate an identical row for nothing. On a `BLOCKED` corridor (an inactive route holding it)
   * re-selecting the same provider is precisely the repair, and it is the only one available: there is no
   * reactivate endpoint, so delete-then-create with the same provider is how that corridor comes back.
   */
  const excludeCode = currentRoute?.is_active ? currentRoute.provider_code : undefined;

  const alternatives = useMemo(
    () => providersForCorridor(cell).filter((provider) => provider.code !== excludeCode),
    [cell, excludeCode],
  );

  const runCreate = async (payload: CreatePaymentRoutePayload) => {
    const created = await createRoute(payload);
    if (created) {
      setPhase("form");
      pendingPayload.current = null;
      onSwitched(corridorLabel);
      return;
    }
    setPhase("orphaned");
  };

  const handleSubmit = async (submitted: RouteFormValues) => {
    if (!currentRoute) return;

    const payload: CreatePaymentRoutePayload = {
      rail: cell.rail,
      country: cell.country,
      currency: cell.currency,
      direction: cell.direction,
      provider_code: submitted.providerCode,
      economic_model: submitted.economicModel,
    };
    pendingPayload.current = payload;

    // A 404 here means someone else already removed the row: the corridor is free, which is what the delete was
    // for, so the creation proceeds instead of reporting a failure that is not one.
    const outcome = await removeRoute(currentRoute.id);
    if (outcome === "error") return;

    await runCreate(payload);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    // Whatever happened, the caller must reload: after a failed switch the corridor is unrouted.
    if (phase === "orphaned") onFinished();
    onClose();
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={t("switch_title")}
      description={t("switch_subtitle")}
      size="default"
      mobileFullscreen
    >
      {phase === "orphaned" ? (
        <div className="space-y-4">
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            heading={t("switch_failed_title")}
            text={t("switch_failed_detail")}
          />
          {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <ColocaButton type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="primary"
              icon={Repeat}
              loading={isSubmitting}
              onClick={() => {
                if (pendingPayload.current) void runCreate(pendingPayload.current);
              }}
            >
              {t("switch_retry")}
            </ColocaButton>
          </div>
        </div>
      ) : (
        <ColocaForm form={form} onSubmit={handleSubmit} className="space-y-5">
          {/* What is being replaced. */}
          <div className="space-y-2 rounded-xl border border-border bg-card p-3">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("switch_current_label")}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <ColocaBadge variant="default">{cell.country}</ColocaBadge>
              <ColocaBadge variant="secondary" className="font-sans">
                {formatRail(cell.rail)}
              </ColocaBadge>
              <span className="font-mono text-xs text-muted-foreground">{cell.currency}</span>
              <ColocaBadge variant="default" className="ml-auto font-sans">
                {labels.direction[cell.direction]}
              </ColocaBadge>
            </div>
            {currentRoute && (
              <p className="text-xs text-muted-foreground">
                {labels.model[currentRoute.economic_model]} ·{" "}
                {t("provider_corridors", { count: cell.providerCorridorCount })}
              </p>
            )}
          </div>

          <ProviderChoiceField
            name="providerCode"
            corridor={cell}
            corridorCountByProvider={corridorCountByProvider}
            excludeCode={excludeCode}
            emptyExcludedText={t("switch_no_alternative")}
          />

          {alternatives.length > 0 && (
            <ColocaChoiceCardField
              name="economicModel"
              label={t("create_model_label")}
              description={t("create_model_helper")}
              columns={1}
              items={[
                {
                  value: EconomicModel.PASS_THROUGH,
                  label: labels.model[EconomicModel.PASS_THROUGH],
                  description: labels.modelHelp[EconomicModel.PASS_THROUGH],
                  icon: <TrendingUp className="size-5 text-success" />,
                },
                {
                  value: EconomicModel.ABSORBED,
                  label: labels.model[EconomicModel.ABSORBED],
                  description: labels.modelHelp[EconomicModel.ABSORBED],
                  icon: <HandCoins className="size-5 text-warning" />,
                },
              ]}
            />
          )}

          {/* The gap is real and short — it must be stated before the operator commits, not after. */}
          <ColocaAlert variant="warning" icon={AlertTriangle} text={t("switch_gap_warning")} />

          {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <ColocaButton type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("cancel")}
            </ColocaButton>
            <ColocaButton
              type="submit"
              variant="primary"
              icon={Repeat}
              loading={isSubmitting}
              disabled={!values.providerCode || alternatives.length === 0}
            >
              {t("switch_confirm")}
            </ColocaButton>
          </div>
        </ColocaForm>
      )}
    </ColocaDialog>
  );
}
